import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import SMCTextInput from '../SMCTextInput';

/**
 * 
 * @param {object} params
 * @param {boolean} params.disabled
 * @param {boolean} params.readOnly
 * @param {(string|number)} params.value
 * @param {function} params.onChange
 * @param {string} params.className
 * @param {string} params.label
 * @param {('start'|'end')} params.labelPosition
 * @param {(string|number)} params.labelWidth
 * @param {string} params.tooltip
 * @param {boolean} params.inline
 * @param {boolean} params.noAutoComplete
 * @param {object} params.style
 * @param {string} params.placeholder
 * @param {(string|number)} params.height
 * @param {*} params.innerRef
 * @param {(string|number)} params.width
 * @param {boolean} params.allowClearAll
 * @param {string} params.suffixIcon
 * @param {function} params.suffixIconOnClick
 * @param {boolean} params.required
 * @param {boolean} params.ignoreDisabledContext
 * @param {boolean} params.allowPasswordPeek
 */
function SMCPasswordInput({
    placeholder,
    disabled,
    noAutoComplete,
    readOnly,
    width,
    value,
    onChange,
    className,
    style,
    allowClearAll,
    height,
    inline,
    label,
    labelPosition,
    labelWidth,
    suffixIcon,
    suffixIconOnClick,
    tooltip,
    innerRef,
    required,
    ignoreDisabledContext,
    allowPasswordPeek,
    ...rest
}) {
    const [isHidingPassword, setIsHidingPassword] = useState(true);
    
    // If allowPasswordPeek is enabled, override the icon showed to eye/hideEye
    const _suffixIcon = useMemo(() => {
        if (allowPasswordPeek) {
            // If password peek is allowed, but the field is disabled or readOnly, then show no icon
            if (disabled || readOnly) {
                return undefined;
            }
            if (isHidingPassword) {
                return 'eye';
            }
            return 'hideEye';
        }
        return suffixIcon;
    }, [allowPasswordPeek, disabled, readOnly, isHidingPassword, suffixIcon]);

    const _suffixIconOnClick = useMemo(() => {
        if (allowPasswordPeek) {
            return () => setIsHidingPassword(val => !val);
        }
        return suffixIconOnClick;
    }, [allowPasswordPeek, suffixIconOnClick]);

    return (
        <SMCTextInput
            className={className}
            disabled={disabled}
            readOnly={readOnly}
            value={value}
            onChange={onChange}
            noAutoComplete={noAutoComplete}
            style={style}
            width={width}
            type={isHidingPassword ? 'password' : 'text'}
            placeholder={placeholder}
            allowClearAll={allowClearAll}
            height={height}
            inline={inline}
            label={label}
            labelPosition={labelPosition}
            labelWidth={labelWidth}
            suffixIcon={_suffixIcon}
            suffixIconOnClick={_suffixIconOnClick}
            tooltip={tooltip}
            innerRef={innerRef}
            required={required}
            ignoreDisabledContext={ignoreDisabledContext}
            {...rest}
        />
    );
}

SMCPasswordInput.propTypes = {
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    className: PropTypes.string,
    label: PropTypes.node,
    labelPosition: PropTypes.oneOf(['start', 'end']),
    labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tooltip: PropTypes.node,
    inline: PropTypes.bool,
    noAutoComplete: PropTypes.bool,
    style: PropTypes.object,
    placeholder: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    innerRef: PropTypes.any,
    allowClearAll: PropTypes.bool,
    suffixIcon: PropTypes.string,
    suffixIconOnClick: PropTypes.func,
    required: PropTypes.bool,
    ignoreDisabledContext: PropTypes.bool,
    allowPasswordPeek: PropTypes.bool
}

export default React.memo(SMCPasswordInput);